import { useLocation, useNavigate } from 'react-router-dom';
import ProductCardRow from './card/ProductCardRow';
import Filter from '../../form/Filter';
import { useCallback, useEffect, useRef, useState } from 'react';
import { categories, useProductStore } from '../../../store/useProduct';
import { useLoginStore } from '../../../store/useLogin';
import queryString from 'query-string';
import { updateUrlWithParams } from '../../../util/util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFolder } from '@fortawesome/free-solid-svg-icons';

/**
 * @author kkakkuro0
 * @date 2024.10.08
 * @description 상품 페이지
 */
const Product = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { role } = useLoginStore();
  const { products, getProducts, getCategories } = useProductStore();
  const [categories, setCategories] = useState<categories[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  const fieldSelectRef = useRef<HTMLSelectElement>(null);
  const orderSelectRef = useRef<HTMLSelectElement>(null);
  const searchTextRef = useRef<HTMLInputElement>(null);

  const fieldOptions = [{ option: '상품명', value: 'productName' }];

  const handleSearch = useCallback(() => {
    const searchText = searchTextRef.current?.value || '';
    const field = fieldSelectRef.current?.value || 'productName';
    const order = orderSelectRef.current?.value || 'desc';
    updateUrlWithParams(
      {
        search: searchText || undefined,
        field: field !== 'productName' ? field : undefined,
        order: order !== 'desc' ? order : undefined,
        categoryId: selectedCategory === 'ALL' ? undefined : selectedCategory,
        all: selectedCategory === 'ALL' ? 'true' : undefined,
      },
      navigate
    );

    getProducts(
      searchText,
      field,
      order as 'asc' | 'desc',
      selectedCategory === 'ALL' ? '' : selectedCategory
    );
  }, [navigate, getProducts, selectedCategory]);

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const search = (parsedQuery.search as string) || '';
    const field = (parsedQuery.field as string) || 'productName';
    const order = (parsedQuery.order as 'asc' | 'desc') || 'desc';
    const categoryId = (parsedQuery.categoryId as string) || '';
    const all = (parsedQuery.all as string) || '';

    // Update input values
    if (searchTextRef.current) searchTextRef.current.value = search;
    if (fieldSelectRef.current) fieldSelectRef.current.value = field;
    if (orderSelectRef.current) orderSelectRef.current.value = order;

    if (all === 'true') {
      setSelectedCategory('ALL');
      getProducts(search, field, order, '');
    } else {
      setSelectedCategory(categoryId);
      getProducts(search, field, order, categoryId);
    }
  }, [location.search, getProducts]);

  useEffect(() => {
    const fetchCategories = async () => {
      const cate = await getCategories();
      setCategories(cate);
    };

    fetchCategories();
  }, [getCategories]);

  // 스켈레톤 데이터
  const skeletonData = Array.from({ length: 8 }, (_, i) => ({
    id: `skeleton${i + 1}`,
    skeleton: true,
  }));

  const openAllProducts = useCallback(() => {
    setSelectedCategory('ALL');
    const searchText = searchTextRef.current?.value || '';
    const field = fieldSelectRef.current?.value || 'productName';
    const order = orderSelectRef.current?.value || 'desc';
    // push history entry for ALL selection
    updateUrlWithParams(
      {
        search: searchText || undefined,
        field: field !== 'productName' ? field : undefined,
        order: order !== 'desc' ? order : undefined,
        all: 'true',
      },
      navigate,
      { replace: false }
    );
    getProducts(searchText, field, order as 'asc' | 'desc', '');
  }, [getProducts, navigate]);

  const changeCategory = useCallback(
    (categoryId: string) => {
      setSelectedCategory(categoryId);
      const searchText = searchTextRef.current?.value || '';
      const field = fieldSelectRef.current?.value || 'productName';
      const order = orderSelectRef.current?.value || 'desc';
      updateUrlWithParams(
        {
          search: searchText || undefined,
          field: field !== 'productName' ? field : undefined,
          order: order !== 'desc' ? order : undefined,
          categoryId: categoryId || undefined,
          all: undefined,
        },
        navigate,
        { replace: false }
      );

      getProducts(searchText, field, order as 'asc' | 'desc', categoryId);
    },
    [navigate, getProducts]
  );

  return (
    <div className="!w-full !min-h-full !flex !flex-col">
      <div className="!flex !justify-between !items-center !pt-4 !pb-4 !border-b !border-gray-200 !mb-4">
        <div className="!flex !items-center !gap-6">
          <Filter
            fieldRef={fieldSelectRef}
            orderRef={orderSelectRef}
            searchRef={searchTextRef}
            fieldOptions={fieldOptions}
            getFilteredData={handleSearch}
          />
        </div>
        {role === 'ROLE_ADMIN' && (
          <button
            type="button"
            onClick={() => navigate('/product/create')}
            className="!px-4 !py-2 !bg-blue-500 hover:!bg-blue-600 !text-white !font-medium !rounded-lg !transition-colors !text-sm"
          >
            상품 등록
          </button>
        )}
      </div>

      {/* 폴더형 카테고리 목록 (카테고리 미선택 시) */}
      {(!selectedCategory || selectedCategory === '') && (
        <div className="!w-full !grid !grid-cols-[repeat(auto-fill,minmax(220px,1fr))] !gap-6 !pb-8">
          {/* 전체 폴더 */}
          <button
            type="button"
            onClick={openAllProducts}
            className="!group !flex !flex-col !items-center !justify-center !p-6 !rounded-2xl !border !border-gray-200 hover:!border-blue-300 !bg-white !shadow-sm hover:!shadow-lg !transition-all hover:!translate-y-[-2px]"
          >
            <div className="!w-full !h-36 !flex !items-center !justify-center !mb-4">
              <FontAwesomeIcon
                icon={faFolder}
                className="!text-yellow-400 group-hover:!text-yellow-500 !transition-colors"
                size="6x"
              />
            </div>
            <div className="!w-full !text-center !text-base !font-semibold !text-gray-800 !truncate">
              전체
            </div>
          </button>

          {categories && categories.length > 0 ? (
            categories.map(category => (
              <button
                key={category.id}
                type="button"
                onClick={() => changeCategory(category.id)}
                className="!group !flex !flex-col !items-center !justify-center !p-6 !rounded-2xl !border !border-gray-200 hover:!border-blue-300 !bg-white !shadow-sm hover:!shadow-lg !transition-all hover:!translate-y-[-2px]"
              >
                <div className="!w-full !h-36 !flex !items-center !justify-center !mb-4">
                  <FontAwesomeIcon
                    icon={faFolder}
                    className="!text-yellow-400 group-hover:!text-yellow-500 !transition-colors"
                    size="6x"
                  />
                </div>
                <div className="!w-full !text-center !text-base !font-semibold !text-gray-800 !truncate">
                  {category.categoryName}
                </div>
              </button>
            ))
          ) : (
            <div className="!col-span-full !text-center !text-gray-500 !py-10">
              카테고리가 없습니다.
            </div>
          )}
        </div>
      )}

      {/* 선택된 카테고리의 상품 목록 */}
      {selectedCategory && selectedCategory !== '' && (
        <>
          <div className="!flex !items-center !justify-between !mb-4">
            <div className="!flex !items-center !gap-3">
              <button
                type="button"
                onClick={() => changeCategory('')}
                className="!px-3 !py-1.5 !rounded-lg !text-sm !bg-gray-100 hover:!bg-gray-200 !text-gray-700 !transition-colors"
              >
                ← 카테고리로 돌아가기
              </button>
              <span className="!text-sm !text-gray-500">
                /{' '}
                {selectedCategory === 'ALL'
                  ? '전체'
                  : categories.find(c => c.id === selectedCategory)
                      ?.categoryName || '선택된 카테고리'}
              </span>
            </div>
          </div>

          <div className="!w-full !grid !grid-cols-[repeat(auto-fill,minmax(240px,1fr))] !gap-5 !pb-10 !flex-1 !content-start">
            {!products && <ProductCardRow products={skeletonData} />}
            {products && products.length > 0 && (
              <ProductCardRow products={products} />
            )}
          </div>

          {products && products.length === 0 && (
            <div
              className="!w-full !max-w-[600px] !h-[400px] !bg-no-repeat !bg-contain !bg-center !bg-white !rounded-2xl !shadow-lg !mx-auto !my-[60px]"
              style={{
                backgroundImage: `url(${require('../../../assets/img/saju_no_data.png')})`,
              }}
            ></div>
          )}
        </>
      )}
    </div>
  );
};

export default Product;
